import Table from '@shared/components/Table';
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { levelOfStructureService } from '@modules/organizationalStructure/levelOfStructure/services/levelOfStructure';
import { structuralGroupService } from '../services/structuralGroups';
import DeleteModal from '@shared/components/DeleteModal';
import StructuralGroupForm from './StructuralGroupForm';
import StructuralGroupsPositions from './StructuralGroupsPositions';
import StructuralGroupTranslation from './StructuralGroupTranslation';
import OccupyingPossition from "./OccupyingPossition"
import ChangeConstCenter from "./ChangeConstCenter"
import StructuralGroupsCostCenter from './StructuralGroupsCostCenter';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import ActionButtons from '@shared/components/ActionButtons';
import { useStatusBadge } from '@shared/components/StatusBadge';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useReportGenerator } from '@shared/hooks/useReportGenerator';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
    setCompanies, setSelectedCompany, setCompaniesLoading, setError,
    fetchCompanies, fetchLevels, setLevels, setSelectedLevel, fetchStructuralGroups, setStructuralGroups, fetchPositions, setPositions, setStructuralGroupsSearchQuery,
    setStructuralGroupsSearchType, setPositionsSearchQuery, setPositionsSearchType, setSelectedRecord, setSelectedData,
    setCheckboxSelectedRecord, setPositionsCheckboxSelectedRecord, setDrillDownData, setIsDrillDownMode, setParentId,
    setBreadcrumbPath, addToBreadcrumbPath, setPositionsDrillDownData, setIsPositionsDrillDownMode, setPositionsBreadcrumbPath,
    addToPositionsBreadcrumbPath, setModalState, setPositionsColumns, setFullCompanyObject, resetToRoot, fetchDrillDownData, fetchPositionsByJobId, setNavigationSource
}
    from '@store/slices/structuralGroupSlice';


export default function StructuralGroups() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { statusBadge } = useStatusBadge();
    const dispatch = useDispatch();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const {
        companies, selectedCompany, companiesLoading, error,
        levels, levelsLoading, selectedLevel,
        structuralGroups, structuralGroupsLoading,
        positions, positionsLoading,
        searchState,
        selectedRecord, selectedData, checkboxSelectedRecord, positionsCheckboxSelectedRecord,
        drillDownData, isDrillDownMode, parentId, breadcrumbPath,
        positionsDrillDownData, isPositionsDrillDownMode, positionsBreadcrumbPath,
        modalState, positionsColumns, fullCompanyObject
    } = useSelector(state => state.structuralGroup)


    // Find the selected level object to get its name
    const selectedLevelObject = levels.find(level => level.id === selectedLevel);

    const structuralGroupsSearchQuery = searchState?.structuralGroups?.query || {};
    const structuralGroupsSearchType = searchState?.structuralGroups?.type || '';
    const positionsSearchQuery = searchState?.positions?.query || {};
    const positionsSearchType = searchState?.positions?.type || '';


    useEffect(() => {
    }, [positionsCheckboxSelectedRecord, isPositionsDrillDownMode]);

    useEffect(() => {
        if (isPositionsDrillDownMode) {
            dispatch(setPositionsColumns([
                { key: 'positionNumber', label: t('organizationalStructure.structuralGroups.table.headers.code'), render: (item) => item.positionNumber || item.originalData?.positionNumber || "" },
                {
                    key: 'positionName', label: t('organizationalStructure.structuralGroups.table.headers.name'), render: (item) => item.positionName || item.originalData?.positionName || ""
                },
                {
                    key: 'ocupantName', label: 'Заемащ позицията', render: (item) => item.ocupantName || item.originalData?.ocupantName || ""
                },
                { key: 'jobDescriptionName', label: t('organizationalStructure.structuralGroups.table.headers.jobDescription'), render: (item) => item.jobDescriptionName || item.originalData?.jobDescriptionName || "" },
            ]));
        } else {
            dispatch(setPositionsColumns([
                { key: 'jobCode', label: t('organizationalStructure.structuralGroups.table.headers.code') },
                { key: 'longDescription', label: t('organizationalStructure.structuralGroups.table.headers.name') },
                { key: 'nkpd', label: t('organizationalStructure.structuralGroups.table.headers.noc') },
                { key: 'positionCount', label: t('organizationalStructure.structuralGroups.table.headers.count') },
                { key: 'hrJobDescription', label: t('organizationalStructure.structuralGroups.table.headers.jobDescription') },
            ]));
        }
    }, [isPositionsDrillDownMode, t, dispatch]);

    useEffect(() => {
        dispatch(fetchCompanies());
    }, [dispatch, adminBranchId]);

    useEffect(() => {
        if (adminBranchId == null) {
            return;
        }

        if (String(selectedCompany) !== String(adminBranchId)) {
            dispatch(setSelectedCompany(adminBranchId));
            dispatch(setSelectedLevel(null));
            dispatch(setFullCompanyObject(null));
        }
    }, [adminBranchId, selectedCompany, dispatch]);

    useEffect(() => {
        if (companies && companies.length > 0 && selectedCompany && !fullCompanyObject) {
            const companyObject = companies.find(c => c.id === selectedCompany);
            if (companyObject) {
                dispatch(setFullCompanyObject(companyObject));
            }
        }
    }, [companies, selectedCompany, fullCompanyObject, dispatch]);

    useEffect(() => {
        const companyId = adminBranchId ?? selectedCompany;

        if (companyId) {
            dispatch(fetchLevels(companyId));
        } else {
            dispatch(setLevels([]));
            dispatch(setSelectedLevel(null));
        }
    }, [selectedCompany, adminBranchId, dispatch]);

    useEffect(() => {
        if (selectedLevel) {
            dispatch(fetchStructuralGroups({ levelId: selectedLevel }));
        } else {
            dispatch(setStructuralGroups([]));
        }

        // Clear drill-down mode but preserve layout by keeping selection
        dispatch(setIsDrillDownMode(false));
        dispatch(setDrillDownData([]));
        dispatch(setParentId(null));
        dispatch(setBreadcrumbPath([]));
        dispatch(setIsPositionsDrillDownMode(false));
        dispatch(setPositionsDrillDownData([]));
        dispatch(setPositionsBreadcrumbPath([]));
        dispatch(setPositionsCheckboxSelectedRecord(null));
        dispatch(setPositions([]));
    }, [selectedLevel, dispatch]);

    useEffect(() => {
        if (structuralGroups && structuralGroups.length > 0 && selectedRecord && !isDrillDownMode) {
            let recordToSelect = structuralGroups.find(record => record.id === selectedRecord.id);
            if (!recordToSelect) {
                recordToSelect = structuralGroups[0];
                dispatch(setSelectedRecord(recordToSelect));
                dispatch(setSelectedData(recordToSelect.id));
                dispatch(setCheckboxSelectedRecord(recordToSelect));
                dispatch(fetchPositions({ structuralGroupId: recordToSelect.id }));
            }
        }
    }, [structuralGroups, selectedRecord, dispatch, isDrillDownMode]);

    useEffect(() => {
        if (selectedData) {
            dispatch(fetchPositions({ structuralGroupId: selectedData }));
        } else {
            dispatch(setPositions([]));
        }
    }, [selectedData, dispatch]);

    useEffect(() => {
        const navigationSource = location.state?.from;

        if (navigationSource === 'sidebar' ||
            navigationSource === 'logout' ||
            !navigationSource) {

            dispatch(resetToRoot());
            dispatch(setSelectedRecord(null));
            dispatch(setSelectedData(null));
            dispatch(setCheckboxSelectedRecord(null));
            dispatch(setPositionsCheckboxSelectedRecord(null));
        }

    }, [location.state?.from, dispatch]);

    const columns = [
        { key: 'hrOrgUnitLevelsId', label: t('organizationalStructure.structuralGroups.table.headers.level') },
        { key: 'code', label: t('organizationalStructure.structuralGroups.table.headers.code') },
        { key: 'longDescription', label: t('organizationalStructure.structuralGroups.table.headers.name') },
        { key: 'translated', label: t('organizationalStructure.structuralGroups.table.headers.transliteration') },
    ];

    const searchOptions = [
        { key: 'title', label: 'title' },
        { key: 'code', label: 'code' },
        { key: 'date', label: 'structuralGroupDate', type: 'date' },
    ];

    const searchOptions2 = [
        { key: 'code', label: 'code' },
        { key: 'name', label: 'name' },
    ];


    const { 
        generatePdfReport: generatePositionsPdf, 
        generateExcelReport: generatePositionsExcel,
        pdfLoading: positionsPdfLoading,
        excelLoading: positionsExcelLoading
    } = useReportGenerator({
        title: t('organizationalStructure.structuralGroups.secondaryTitle'),
        columns: [
            { key: 'positionNumber', label: t('organizationalStructure.structuralGroups.table.headers.positionNumber') },
            { key: 'hrJobTitle', label: t('organizationalStructure.structuralGroups.table.headers.positionTitle') },
            { key: 'costCenterName', label: t('organizationalStructure.structuralGroups.positionsForm.costCenter') },
            { 
                label: t('organizationalStructure.structuralGroups.table.headers.workLocation'),
                children: [
                    { key: 'locationCode', label: t('organizationalStructure.structuralGroups.table.headers.code') },
                    { key: 'locationName', label: t('organizationalStructure.structuralGroups.table.headers.name') }
                ]
            },
            { key: 'titularName', label: t('organizationalStructure.structuralGroups.table.headers.titularName') },
            { key: 'ocupantName', label: t('organizationalStructure.structuralGroups.table.headers.occupantName') },
            { key: 'nkpd', label: t('organizationalStructure.structuralGroups.table.headers.noc') },
            { key: 'ftePercent', label: t('organizationalStructure.structuralGroups.positionsForm.ftePercent') },
            { key: 'budget', label: t('organizationalStructure.structuralGroups.positionsForm.budget') },
            { 
                key: 'status', 
                label: t('organizationalStructure.structuralGroups.positionsForm.status'),
                render: (item) => item.status === 'y' ? 'Постоянна' : item.status === 'n' ? 'Временна' : item.status
            },
            { key: 'hoursOfWork', label: t('organizationalStructure.structuralGroups.positionsForm.workTime') },
            { key: 'openDate', label: t('organizationalStructure.structuralGroups.positionsForm.openDate') },
            { key: 'closeDate', label: t('organizationalStructure.structuralGroups.positionsForm.closeDate') },
            { key: 'jobDescription', label: t('organizationalStructure.structuralGroups.table.headers.jobDescription') },
            {
                label: t('organizationalStructure.structuralGroups.table.headers.heading'),
                children: [
                    { key: 'orgUnitCode', label: t('organizationalStructure.structuralGroups.table.headers.code') },
                    { key: 'orgUnitBranch', labelKey: 'orgUnitBranch' },
                    { key: 'orgUnitName', label: t('organizationalStructure.structuralGroups.table.headers.direction') }
                ]
            }
        ],
        fileName: 'positions_report',
        companyName: fullCompanyObject?.name || '',
        reportService: selectedRecord ? () => structuralGroupService.generateReports(selectedRecord.id) : null,
        customStyles: {
            tableFontSize: '5px',
            pageSize: 'A3',
            pageMargins: [20, 70, 20, 40]
        }
    });

    // Report generator for org unit hierarchy
    const { 
        generatePdfReport: generateHierarchyPdf, 
        generateExcelReport: generateHierarchyExcel,
        pdfLoading: hierarchyPdfLoading,
        excelLoading: hierarchyExcelLoading
    } = useReportGenerator({
        title: t('organizationalStructure.structuralGroups.title') + ' - ' + (fullCompanyObject?.name || ''),
        columns: [
            { key: 'level', label: t('organizationalStructure.structuralGroups.table.headers.level') },
            { key: 'structure', label: t('organizationalStructure.structuralGroups.table.headers.code') },
            { key: 'levelCode', label: t('organizationalStructure.structuralGroups.table.headers.structure') },
            { key: 'hierarchy', label: t('organizationalStructure.structuralGroups.form.subordination') },
            { key: 'officeNo', label: t('organizationalStructure.structuralGroups.table.headers.code') },
            { key: 'costCenterName', label: t('organizationalStructure.structuralGroups.form.costCenter') },
            { key: 'additionalLeave', label: t('organizationalStructure.structuralGroups.positionsForm.addPayedLeave') },
            { key: 'locationName', label: t('organizationalStructure.structuralGroups.table.headers.workLocation') },
            { key: 'openDate', label: t('common.fromDate') },
            { key: 'closeDate', label: t('common.toDate') },
        ],
        fileName: 'org_unit_hierarchy_report',
        companyName: fullCompanyObject?.name || '',
        reportService: (selectedRecord && selectedCompany) 
            ? () => structuralGroupService.getOrgUnitHierarchy(selectedRecord.id, selectedCompany)
            : null,
        customStyles: {
            tableFontSize: '7px'
        }
    });


    const actionButtons = (
        <>
            <ActionButtons
                buttons={[
                    // Only include ADD button when in drill-down mode
                    ...(isDrillDownMode ? [{
                        ...COMMON_BUTTONS.ADD,
                        onClick: () => dispatch(setModalState({ visible: true, type: 'add' })),
                    }] : []),
                    {
                        ...COMMON_BUTTONS.EDIT,
                        onClick: async () => {
                            let editData = checkboxSelectedRecord;

                            if (checkboxSelectedRecord) {
                                try {
                                    const fullRecordData = await structuralGroupService.getStructuralGroupById(checkboxSelectedRecord.id);
                                    editData = fullRecordData;
                                } catch (error) {
                                    console.error('❌ Error fetching full record data:', error);
                                    editData = checkboxSelectedRecord;
                                }
                            }
                            dispatch(setModalState({ visible: true, type: 'edit', editData }));
                        }
                    },
                    {
                        ...COMMON_BUTTONS.DELETE,
                        onClick: () => handleOpenModal('delete')
                    },
                    {
                        ...COMMON_BUTTONS.TRANSLATE,
                        onClick: () => dispatch(setModalState({ visible: true, type: 'translations', editData: checkboxSelectedRecord }))
                    },
                    {
                        ...COMMON_BUTTONS.COST_CENTERS,
                        onClick: () => dispatch(setModalState({ visible: true, type: 'structuralGroupsCostCenter', editData: checkboxSelectedRecord }))
                    },
                
                    // Only show hierarchy reports in drill-down mode
                    ...(isDrillDownMode ? [
                        {
                            ...COMMON_BUTTONS.MOVE,
                            onClick: () => {
                                if (checkboxSelectedRecord) {
                                    navigate('/structural-group-moving', { state: { structuralGroup: checkboxSelectedRecord } });
                                }
                            },
                            showWhen: 'whenSelected'
                        },
                        {
                        ...COMMON_BUTTONS.DOWNLOAD_PDF,
                        onClick: generateHierarchyPdf,
                        disabled: hierarchyPdfLoading || !selectedRecord,
                        icon: hierarchyPdfLoading ? faSpinner : COMMON_BUTTONS.DOWNLOAD_PDF.icon,
                        iconProps: hierarchyPdfLoading ? { spin: true } : {},
                        title: hierarchyPdfLoading ? "Генериране на PDF йерархия..." : "PDF Йерархия",
                        showWhen: 'always'
                    },
                  
                    {
                        ...COMMON_BUTTONS.EXCEL,
                        onClick: generateHierarchyExcel,
                        disabled: hierarchyExcelLoading || !selectedRecord,
                        icon: hierarchyExcelLoading ? faSpinner : COMMON_BUTTONS.EXCEL.icon,
                        iconProps: hierarchyExcelLoading ? { spin: true } : {},
                        title: hierarchyExcelLoading ? "Генериране на Excel йерархия..." : "Excel Йерархия",
                        showWhen: 'always'
                    }] : [])
                ]}
                selectedRecord={checkboxSelectedRecord}
            />
        </>
    );

    const actionButtons2 = isPositionsDrillDownMode ? (
        <>
            <ActionButtons
                buttons={[
                    {
                        ...COMMON_BUTTONS.EDIT,
                        onClick: () => dispatch(setModalState({ visible: true, type: 'editPosition', editData: positionsCheckboxSelectedRecord }))
                    },
                    {
                        ...COMMON_BUTTONS.DELETE,
                        onClick: () => dispatch(setModalState({ visible: true, type: 'deletePosition', editData: positionsCheckboxSelectedRecord }))
                    },
                    {
                        ...COMMON_BUTTONS.COST_CENTERS,
                        onClick: () => dispatch(setModalState({ visible: true, type: 'costCenter', editData: positionsCheckboxSelectedRecord }))
                    }
                ]}
                selectedRecord={positionsCheckboxSelectedRecord}
            />

            <Link to="/structural-group-rights"
                state={{
                    structuralGroup: positionsCheckboxSelectedRecord,
                    orgUnit: selectedRecord,
                    companyId: selectedCompany
                }}
                className={`icon-button ${!positionsCheckboxSelectedRecord ? 'disabled-link' : ''}`}
                disabled={!positionsCheckboxSelectedRecord}
                title={t('common.rights')}
            >
                <img src='/images/icons/rights.png' alt='rights' style={{ width: '24px' }} />
            </Link>
            <button
                className={`icon-button ${!positionsCheckboxSelectedRecord ? 'disabled-link' : ''}`}
                disabled={!positionsCheckboxSelectedRecord}
                onClick={(e) => dispatch(setModalState({ visible: true, type: 'occupyingPossition', editData: positionsCheckboxSelectedRecord }))}
                title={t('organizationalStructure.structuralGroups.occupyingPossition.title')}
            >
                <img src='/images/icons/position.png' alt='position' style={{ width: '24px' }} />
            </button>
        </>
    ) : (
        <>
            <ActionButtons
                buttons={[
                    {
                        ...COMMON_BUTTONS.ADD,
                        onClick: () => handleOpenModal('positions')
                    },
                    {
                        ...COMMON_BUTTONS.DOWNLOAD_PDF,
                        onClick: generatePositionsPdf,
                        disabled: positionsPdfLoading || !selectedRecord,
                        icon: positionsPdfLoading ? faSpinner : COMMON_BUTTONS.DOWNLOAD_PDF.icon,
                        iconProps: positionsPdfLoading ? { spin: true } : {},
                        title: positionsPdfLoading ? "Генериране на PDF..." : "common.downloadPDF",
                        showWhen: 'always'
                    },
                    {
                        ...COMMON_BUTTONS.EXCEL,
                        onClick: generatePositionsExcel,
                        disabled: positionsExcelLoading || !selectedRecord,
                        icon: positionsExcelLoading ? faSpinner : COMMON_BUTTONS.EXCEL.icon,
                        iconProps: positionsExcelLoading ? { spin: true } : {},
                        title: positionsExcelLoading ? "Генериране на Excel..." : "common.downloadExcel",
                        showWhen: 'always'
                    }
                ]}
                selectedRecord={null}
            />
        </>
    );


    const handleDelete = async (record) => {
        try {
            await structuralGroupService.deleteStructuralGroup(record.id);

            if (isDrillDownMode && parentId) {
                // When in drill-down mode, fetch children of the current parent
                await dispatch(fetchDrillDownData({ selectedCompany, parentId }));
            } else if (selectedLevel) {
                // When not in drill-down mode, fetch all records for the selected level
                const response = await levelOfStructureService.getOrgUnitsByLevel(selectedLevel);
                dispatch(setStructuralGroups(response));
            }

            // Only clear checkbox selection, keep layout intact (same as handleDeletePosition)
            dispatch(setCheckboxSelectedRecord(null));
            notifySuccess(t('common.deletedSuccessfully'));
            handleCloseModal();
        } catch (error) {
            const errorMessage = error.response?.data?.message;
            notifyError(errorMessage);
            console.error('Error deleting structural group:', error);

        }
    };

    const handleConfirmDelete = async () => {
        if (selectedRecord) {
            await handleDelete(selectedRecord);
        }
    };

    const handleDeletePosition = async (record) => {
        try {
            await structuralGroupService.deleteStructuralGroupPosition(record.id);

            if (isPositionsDrillDownMode && selectedRecord) {
                const currentBreadcrumb = positionsBreadcrumbPath[positionsBreadcrumbPath.length - 1];
                if (currentBreadcrumb && currentBreadcrumb.position) {
                    const response = await structuralGroupService.getPositionsByJobId(selectedRecord.id, currentBreadcrumb.position.id);
                    const transformedData = response.map(item => ({
                        id: item.id,
                        name: 'Заемащ позицията',
                        hrJobDescription: item.jobDescriptionName || 'N/A',
                        hrJob: item.hrJob,
                        hrOrgUnit: item.hrOrgUnit,
                        ocupationHistory: item.ocupationHistory,
                        originalData: item,
                        positionNumber: item.positionNumber
                    }));
                    dispatch(setPositionsDrillDownData(transformedData));
                }
            }

            dispatch(setPositionsCheckboxSelectedRecord(null));
            notifySuccess(t('common.deletedSuccessfully'));
            handleCloseModal();
        } catch (error) {
            console.error('Error deleting position:', error);
            const errorMessage = error.response?.data?.message;
            notifyError(errorMessage);
        }
    };

    const handleConfirmDeletePosition = async () => {
        if (positionsCheckboxSelectedRecord) {
            await handleDeletePosition(positionsCheckboxSelectedRecord);
        }
    };


    const handleSelectRecord = (record) => {
        dispatch(setSelectedRecord(record));
        dispatch(setSelectedData(record.id));
    };

    const handleSearch = () => {
        if (selectedLevel) {
            const searchParams = {};
            if (structuralGroupsSearchQuery.code) {
                searchParams.code = structuralGroupsSearchQuery.code;
            }
            if (structuralGroupsSearchQuery.title) {
                searchParams.longDescription = structuralGroupsSearchQuery.title;
            }
            if (structuralGroupsSearchQuery.date) {
                searchParams.date = structuralGroupsSearchQuery.date;
            }
            dispatch(fetchStructuralGroups({ levelId: selectedLevel, searchParams }));
        }
    };
    const handleCompanyChange = (companyId) => {
        dispatch(setSelectedCompany(companyId));
    };

    const handleLevelChange = (levelId) => {
        dispatch(setSelectedLevel(levelId));
    };

    const handlePositionsSearch = () => {
        if (selectedData) {
            const searchParams = {};
            if (positionsSearchQuery.code) {
                searchParams.code = positionsSearchQuery.code;
            }
            if (positionsSearchQuery.name) {
                searchParams.name = positionsSearchQuery.name;
            }
            dispatch(fetchPositions({ structuralGroupId: selectedData, searchParams }));
        }
    };

    const handleOpenModal = (type) => {
        if (type === 'positions') {
            dispatch(setModalState({ visible: true, type, editData: null }));
        } else {
            dispatch(setModalState({ visible: true, type, editData: selectedRecord }));
        }
    };

    const handleCloseModal = () => {
        dispatch(setModalState({ visible: false, type: null, editData: null }));
    };

    const handleFormSubmit = async (data) => {
        try {
            if (modalState.type === 'edit') {
                await structuralGroupService.updateStructuralGroup(selectedRecord.id, data);
                notifySuccess(t('common.updatedSuccessfully'));
            } else if (modalState.type === 'editPosition') {
                await structuralGroupService.updateStructuralGroupPosition(modalState.editData.id, data);
                notifySuccess(t('common.updatedSuccessfully'));
            } else if (modalState.type === 'positions') {
                await structuralGroupService.createStructuralGroupPositions(data);
                notifySuccess(t('common.createdSuccessfully'));
            } else {
                await structuralGroupService.createStructuralGroup(data);
                notifySuccess(t('common.createdSuccessfully'));
            }

            // Only refresh structural groups data for structural group operations (not position operations)
            if (modalState.type !== 'positions' && modalState.type !== 'editPosition') {
                if (isDrillDownMode && parentId) {
                    // When in drill-down mode, refresh children of the current parent
                    await dispatch(fetchDrillDownData({ selectedCompany, parentId }));
                } else if (selectedLevel) {
                    // When not in drill-down mode, refresh all records for the selected level
                    const response = await levelOfStructureService.getOrgUnitsByLevel(selectedLevel);
                    dispatch(setStructuralGroups(response));
                }

                // Only clear checkbox selection for structural group operations
                dispatch(setCheckboxSelectedRecord(null));
            }

            handleCloseModal();
        } catch (error) {
            console.error('Error saving structural group:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    const handleCheckboxChange = async (index) => {
        const currentData = isDrillDownMode ? drillDownData : structuralGroups;

        if (index >= 0 && index < currentData.length) {
            const structuralGroup = currentData[index];
            const newCheckboxSelection = checkboxSelectedRecord?.id === structuralGroup.id ? null : structuralGroup;
            dispatch(setCheckboxSelectedRecord(newCheckboxSelection));
            dispatch(setSelectedRecord(newCheckboxSelection));
            dispatch(setSelectedData(newCheckboxSelection?.id || null));
        }
    };


    const handlePositionsRowClick = async (index) => {
        const currentData = isPositionsDrillDownMode ? positionsDrillDownData : positions;
        
        if (index >= 0 && index < currentData.length) {
            const position = currentData[index];

            // If already in drill-down mode, just select the row without fetching
            if (isPositionsDrillDownMode) {
                // Use positionNumber as unique identifier since id might be undefined
                const currentSelectionKey = positionsCheckboxSelectedRecord?.positionNumber;
                const clickedPositionKey = position.positionNumber;
                const newCheckboxSelection = currentSelectionKey === clickedPositionKey ? null : position;
                dispatch(setPositionsCheckboxSelectedRecord(newCheckboxSelection));
                return;
            }

            try {
                const response = await structuralGroupService.getPositionsByJobId(selectedRecord.id, position.id);

                const transformedData = response.map(item => {
                        const transformed = {
                            id: item.id,
                            name: 'Заемащ позицията',
                            hrJobDescription: item.jobDescriptionName || 'N/A',
                            hrJob: item.hrJob,
                            hrOrgUnit: item.hrOrgUnit,
                            ocupationHistory: item.ocupationHistory,
                            originalData: item,
                            positionNumber: item.positionNumber
                        };
                        return transformed;
                    });

                dispatch(setPositionsDrillDownData(transformedData));
                dispatch(setIsPositionsDrillDownMode(true));
                dispatch(addToPositionsBreadcrumbPath({
                    jobCode: position.jobCode,
                    title: position.longDescription || position.jobCode,
                    position: position
                }));
            } catch (err) {
                console.error('❌ Error fetching positions drill-down data:', err);
                dispatch(setPositionsDrillDownData([]));
                dispatch(setIsPositionsDrillDownMode(false));
            }
        } else {
            console.error('Invalid index:', index, 'Data length:', currentData.length);
        }
    };

    const handleRowClick = async (index) => {
        const currentData = isDrillDownMode ? drillDownData : structuralGroups;

        if (index >= 0 && index < currentData.length) {
            const structuralGroup = currentData[index];

            // Always fetch children data when clicking on a record
            try {
                const response = await dispatch(fetchDrillDownData({
                    selectedCompany,
                    parentId: structuralGroup.id
                })).unwrap();

                // Set drill down mode and update first table with children
                dispatch(setIsDrillDownMode(true));
                dispatch(setSelectedData(structuralGroup.id));
                dispatch(setSelectedRecord(structuralGroup));
                dispatch(setCheckboxSelectedRecord(null)); // Clear checkbox selection when entering drill-down
                dispatch(setParentId(structuralGroup.id));
                dispatch(addToBreadcrumbPath({
                    id: structuralGroup.id,
                    title: structuralGroup.longDescription || structuralGroup.code,
                    level: structuralGroup.level
                }));

                // Also fetch positions for the second table
                dispatch(fetchPositions({ structuralGroupId: structuralGroup.id }));

            } catch (err) {
                console.error('Error fetching drill-down data:', err);
                dispatch(setDrillDownData([]));
                dispatch(setIsDrillDownMode(false));

                // Even if drill down fails, still select the record and fetch positions
                dispatch(setSelectedData(structuralGroup.id));
                dispatch(setSelectedRecord(structuralGroup));
                dispatch(fetchPositions({ structuralGroupId: structuralGroup.id }));
            }
        } else {
            console.error('Invalid index:', index, 'Data length:', currentData.length);
        }
    };

    const handleBreadcrumbClick = async (breadcrumbItem, index) => {
        try {
            if (breadcrumbItem.id === 'root' || index === 0) {
                dispatch(resetToRoot());
            } else {
                const actualIndex = index - 1;
                const targetItem = breadcrumbPath[actualIndex];

                if (targetItem) {
                    await dispatch(fetchDrillDownData({
                        selectedCompany,
                        parentId: targetItem.id
                    })).unwrap();
                    dispatch(setIsDrillDownMode(true));
                    dispatch(setSelectedData(targetItem.id));
                    dispatch(setSelectedRecord(targetItem));
                    dispatch(setCheckboxSelectedRecord(null)); // Clear checkbox selection when navigating
                    dispatch(setParentId(targetItem.id));
                    dispatch(setBreadcrumbPath(breadcrumbPath.slice(0, actualIndex + 1)));

                    // Reset positions drill-down mode when navigating to parent level
                    dispatch(setIsPositionsDrillDownMode(false));
                    dispatch(setPositionsDrillDownData([]));
                    dispatch(setPositionsCheckboxSelectedRecord(null));
                    dispatch(setPositionsBreadcrumbPath([]));

                    dispatch(fetchPositions({ structuralGroupId: targetItem.id }));
                }
            }
        } catch (err) {
            console.error('Error navigating to breadcrumb level:', err);
            notifyError(t('common.errorMessage'));
        }
    };

  
    const handleStructuralGroupsSearchQueryChange = useMemo(() => (query) => {
        dispatch(setStructuralGroupsSearchQuery(query));
    }, [dispatch]);

    const handleStructuralGroupsSearchTypeChange = useMemo(() => (type) => {
        dispatch(setStructuralGroupsSearchType(type));
    }, [dispatch]);

    const handlePositionsSearchQueryChange = useMemo(() => (query) => {
        dispatch(setPositionsSearchQuery(query));
    }, [dispatch]);

    const handlePositionsSearchTypeChange = useMemo(() => (type) => {
        dispatch(setPositionsSearchType(type));
    }, [dispatch]);


    return (
        <>
            <div className={`large-table ${isDrillDownMode ? 'width-50 me-2' : 'width-100'}`}>
                <Table
                    title={isDrillDownMode ? t("organizationalStructure.structuralGroups.title") : t("organizationalStructure.structuralGroups.headingTitle")}
                    searchBarProps={{ flexDirection: 'column' }}
                    columns={columns}
                    data={isDrillDownMode ? drillDownData : structuralGroups}
                    loading={structuralGroupsLoading}
                    selectedData={checkboxSelectedRecord?.id}
                    onDelete={handleDelete}
                    handleSearchClick={handleSearch}
                    getData={() => { }}
                    searchOptions={searchOptions}
                    searchQuery={structuralGroupsSearchQuery}
                    setSearchQuery={handleStructuralGroupsSearchQueryChange}
                    setSearchType={handleStructuralGroupsSearchTypeChange}
                    companyOptions={companies}
                    companiesLoading={companiesLoading}
                    showPageSizeSelector={false}
                    showPagination={false}
                    showCompanySelect={true}
                    showLevelSelect={true}
                    showPreviousPage={selectedRecord ? false : true}
                    actionButtons={actionButtons}
                    selectedCompany={selectedCompany}
                    setSelectedCompany={handleCompanyChange}
                    selectedLevel={selectedLevel}
                    setSelectedLevel={handleLevelChange}
                    levelOptions={levels}
                    levelsLoading={levelsLoading}
                    handleCheckboxChange={handleCheckboxChange}
                    handleRowClick={handleRowClick}
                    showCheckbox={true}
                    showBreadcrumb={isDrillDownMode}
                    renderCell={(item, column) => statusBadge(item, column)}
                    breadcrumbPath={isDrillDownMode ? [
                        {
                            id: 'root',
                            title: (() => {
                                const levelTitle = selectedLevelObject?.longDescription || selectedLevelObject?.name;
                                const companyTitle = fullCompanyObject?.name;
                                return companyTitle || levelTitle || 'Root';
                            })()
                        },
                        ...breadcrumbPath
                    ] : []}
                    onBreadcrumbClick={isDrillDownMode ? handleBreadcrumbClick : null}
                />
            </div>
            {isDrillDownMode && (
                <div className="small-table width-50 me-2">
                    <Table
                        title={t("organizationalStructure.structuralGroups.secondaryTitle")}
                        searchBarProps={{ flexDirection: 'column' }}
                        columns={positionsColumns}
                        data={isPositionsDrillDownMode ? positionsDrillDownData : positions}
                        loading={positionsLoading}
                        selectedData={isPositionsDrillDownMode
                            ? (positionsCheckboxSelectedRecord ? positionsCheckboxSelectedRecord.positionNumber : null)
                            : (positionsCheckboxSelectedRecord ? positionsCheckboxSelectedRecord.jobCode : null)
                        }
                        checkboxField={isPositionsDrillDownMode ? "positionNumber" : "jobCode"}
                        onSelect={handleSelectRecord}
                        showPagination={false}
                        showPageSizeSelector={false}
                        searchOptions={searchOptions2}
                        searchQuery={positionsSearchQuery}
                        setSearchQuery={handlePositionsSearchQueryChange}
                        setSearchType={handlePositionsSearchTypeChange}
                        handleSearchClick={handlePositionsSearch}
                        getData={() => { }}
                        actionButtons={actionButtons2}
                        showPreviousPage={false}
                        showCheckbox={false}
                        handleRowClick={handlePositionsRowClick}
                    />
                </div>
            )}
            {modalState.visible && modalState.type === 'add' && (
                <StructuralGroupForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.editData}
                />
            )}
            {modalState.visible && modalState.type === 'positions' && (
                <StructuralGroupsPositions
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.editData}
                    hrOrgUnitId={selectedRecord.id}
                    modalType="add"
                    onSuccess={() => {
                        if (selectedData) {
                            dispatch(fetchPositions({ structuralGroupId: selectedData }));
                        }
                    }}

                />
            )}
            {modalState.visible && modalState.type === 'edit' && (
                <StructuralGroupForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.editData}
                />
            )}
            {modalState.visible && modalState.type === 'editPosition' && (
                <StructuralGroupsPositions
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.editData}
                    hrOrgUnitId={selectedRecord.id}
                    modalType="edit"
                    onSuccess={() => {
                        if (isPositionsDrillDownMode && selectedRecord) {
                            const currentBreadcrumb = positionsBreadcrumbPath[positionsBreadcrumbPath.length - 1];
                            if (currentBreadcrumb && currentBreadcrumb.position) {
                                dispatch(fetchPositionsByJobId({
                                    selectedRecordId: selectedRecord.id,
                                    positionId: currentBreadcrumb.position.id
                                }));
                            }
                        } else if (selectedData) {
                            dispatch(fetchPositions({ structuralGroupId: selectedData }));
                        }
                    }}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.longDescription}
                    entityType="record"
                />
            )}
            {modalState.visible && modalState.type === 'deletePosition' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDeletePosition}
                    name={positionsCheckboxSelectedRecord?.positionNumber || positionsCheckboxSelectedRecord?.name}
                    entityType="record"
                />
            )}
            {modalState.visible && modalState.type === 'translations' && (
                <StructuralGroupTranslation
                    onClose={handleCloseModal}
                    structuralGroup={selectedRecord}
                    onSuccess={() => {
                        if (isDrillDownMode && parentId) {
                            // When in drill-down mode, refresh children of the current parent
                            dispatch(fetchDrillDownData({ selectedCompany, parentId }));
                        } else if (selectedLevel) {
                            // When not in drill-down mode, refresh all records for the selected level
                            dispatch(fetchStructuralGroups({ levelId: selectedLevel }));
                        }
                    }}
                />
            )}
            {modalState.visible && modalState.type === 'occupyingPossition' && (
                <OccupyingPossition
                    onClose={handleCloseModal}
                    editData={modalState.editData}
                    onSuccess={() => {
                        if (isPositionsDrillDownMode && selectedRecord) {
                            const currentBreadcrumb = positionsBreadcrumbPath[positionsBreadcrumbPath.length - 1];
                            if (currentBreadcrumb && currentBreadcrumb.position) {
                                dispatch(fetchPositionsByJobId({
                                    selectedRecordId: selectedRecord.id,
                                    positionId: currentBreadcrumb.position.id
                                }));
                            }
                        } else if (selectedData) {
                            dispatch(fetchPositions({ structuralGroupId: selectedData }));
                        }
                    }}
                />
            )}
            {modalState.visible && modalState.type === 'costCenter' && (
                <ChangeConstCenter
                    onClose={handleCloseModal}
                    editData={modalState.editData}
                    companyId={selectedCompany}
                />
            )}
            {modalState.visible && modalState.type === 'structuralGroupsCostCenter' && (
                <StructuralGroupsCostCenter
                    onClose={handleCloseModal}
                    editData={modalState.editData}
                    companyId={selectedCompany}
                />
            )}
        </>
    );
}
